/* eslint-disable max-lines */
/* eslint-disable import/no-named-as-default-member -- atoms accessed by member */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import _ from 'lodash';
import { tippy } from '@tippyjs/react';
import { uniqueId } from 'filestack-js';
import { datadogRum } from '@datadog/browser-rum';
import { Honeybadger } from '@honeybadger-io/react';
import { useQueryClient } from '@tanstack/react-query';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { getPracticeModules } from 'api/Practice';
import { requestApi, setActiveSession } from 'api/Api';

import { ia, io } from 'lib/helpers/utility';
import { socketOff, socketOn } from 'lib/clients/socket';
import { clearStoragePreserveKiosk } from 'lib/helpers/kiosk';
import { useSmartNavigate } from 'lib/hooks/useSmartNavigate';
import { findOpenAndCloseClinicHours } from 'lib/helpers/workingHours';
import { getSessionReplayAttrs } from 'lib/helpers/intercomSessionReplay';
import {
  bootPatientIntercom,
  shutdownPatientIntercom,
  syncPatientIntercomLauncher
} from 'lib/helpers/intercomPatient';
import {
  clinicalNoteSections,
  appointmentStatuses as defaultAptStatuses,
  defaultPracticeSettings,
  defaultSettings
} from 'lib/constants/constants';

import state from './state';
import adminState from './admin/lib/adminState';
import practiceState from './practice/practiceState';
import connectToSocket from './sockets/connectToSocket';

export default function Utility() {
  const [user_state, setUserState] = useRecoilState(state.userState);
  const [practice_state] = useRecoilState(practiceState.current_member);
  const [currentPractice, setCurrentPractice] = useRecoilState(practiceState.currentPractice);
  const [admin_state, setAdminState] = useRecoilState(adminState.current_admin);
  const setAppointmentStatuses = useSetRecoilState(practiceState.appointmentStatuses);
  const [, setPermissions] = useRecoilState(state.permissions);
  const [modules, setModules] = useRecoilState(state.modulesState);
  const [, setOwnerPractices] = useRecoilState(practiceState.ownerPractices);
  const [settings, setSettings] = useRecoilState(state.settings);
  const [hasBeenAuthChecked, setHasBeenAuthChecked] = useRecoilState(state.hasBeenAuthChecked);
  const [ownerSelectedLocation, setOwnerSelectedLocation] = useRecoilState(
    practiceState.ownerSelectedLocation
  );
  const setKiosk = useSetRecoilState(state.kioskState);
  const setAppointmentFilters = useSetRecoilState(practiceState.calendarFilters);
  const setSocketSessionToken = useSetRecoilState(state.socketSessionTokenState);
  const queryClient = useQueryClient();
  const { navigateTo, navigate, locationState } = useSmartNavigate();
  const isSelfScheduling = locationState?.selfSchedulingRedirect;
  const hasStepsCompleted = localStorage.getItem('stepsCompleted');
  const location = useLocation() || {};
  const socket = useRecoilValue(state.socketState);

  const checkSessionStorage = async () => {
    let sessionClientId = sessionStorage.getItem('sessionClientId');

    if (!sessionClientId) {
      sessionClientId = uniqueId(32);
      sessionStorage.setItem('sessionClientId', sessionClientId);
    }

    return sessionClientId;
  };

  const checkUserAndSessionClientId = async () => {
    const sessionClientId = await checkSessionStorage();
    userChecks(sessionClientId);
  };

  useEffect(() => {
    return () => {
      if (socket?.connected) socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!hasBeenAuthChecked.checked) {
      checkUserAndSessionClientId();
    }
    if (ownerSelectedLocation) {
      checkToken();
    }
  }, [hasBeenAuthChecked, ownerSelectedLocation]);

  useEffect(() => {
    try {
      let newLocation = '';
      if (location?.pathname?.includes('/portal')) {
        newLocation = 'portal';
      } else if (location?.pathname?.includes('/myportal')) {
        newLocation = 'myportal';
      } else if (location?.pathname?.includes('/admin')) {
        newLocation = 'admin';
      }
      document.documentElement.classList.remove('portal', 'myportal', 'admin');
      if (newLocation) document.documentElement.classList.add(newLocation);
    } catch (error) {
      console.error(error);
    }
  }, [location]);

  useEffect(() => {
    if (!location?.pathname.includes('book') && !isSelfScheduling && hasStepsCompleted) {
      clearAppointmentBook();
    }
  }, [location, isSelfScheduling, hasStepsCompleted]);

  useEffect(() => {
    getPermissions();
    getModules();
  }, [user_state?.id, practice_state, admin_state]);

  useEffect(() => {
    const path = location?.pathname.split(/\//g);
    if (path && path[1] !== 'admin') {
      getSettings();
    }
  }, [user_state?.id, practice_state]);

  const { pathname } = useLocation();

  useEffect(() => {
    pathname.includes('login') && queryClient.invalidateQueries();
  }, [pathname]);

  // Visiting /kiosk/* converts the browser into a kiosk device (sets the
  // myriadIsKiosk flag), so any booted patient messenger must be killed here.
  useEffect(() => {
    if (pathname.startsWith('/kiosk')) shutdownPatientIntercom();
    else syncPatientIntercomLauncher(pathname);
  }, [pathname]);

  const clearAppointmentBook = async () => {
    await requestApi({
      url: '/api/patient/appointment/self_schedule',
      params: { type: 'deleteCookie' },
      navigate
    });
  };

  useEffect(() => {
    socketOn('permission-update', (data) => {
      const permissions = data.permissions;
      const navIntent = sessionStorage.getItem('navIntent');
      if (navIntent !== '/checkin') {
        setPermissions({ ...permissions, loaded: true });
        setUserState((prev) => ({ ...prev, authenticated: false, logged_in: false }));
        setHasBeenAuthChecked((prev) => ({ ...prev, checked: false }));
      } else {
        // Check-in handoff: a full-page load into /checkin re-bootstraps as the
        // patient. Don't apply the wiped permissions here — doing so bounces the
        // staff off the portal (to /portal/dashboard) during the swap.
        sessionStorage.setItem('permissionUpdatePending', '1');
      }
    });

    return () => {
      socketOff('permission-update');
    };
  }, []);

  useEffect(() => {
    const pending = sessionStorage.getItem('permissionUpdatePending');
    if (!pending) return;

    const navIntent = sessionStorage.getItem('navIntent');
    const isInIntendedPath = location.pathname.startsWith(navIntent);

    if (isInIntendedPath) {
      setUserState((prev) => ({ ...prev, authenticated: false, logged_in: false }));
      setHasBeenAuthChecked((prev) => ({ ...prev, checked: false, isImpersonate: false }));
      sessionStorage.removeItem('navIntent');
      sessionStorage.removeItem('permissionUpdatePending');
      window.location.reload();
    }
  }, [location.pathname]);

  const userChecks = async (sessionClientId) => {
    if (hasBeenAuthChecked.isImpersonate) {
      await getAdminData();
      setHasBeenAuthChecked(() => ({ isImpersonate: false, checked: true }));
      return;
    }

    // get current path
    const path = location?.pathname.split(/\//g);
    const isAdminPath = path[1] === 'admin';

    // admin_state is optimistically hydrated from localStorage (logged_in:true)
    // so the admin UI renders instantly on reload. BUT the socket connection and
    // a fresh session validation live inside getAdminData() (connectToSocket) and
    // are NOT persisted across reloads — only the logged_in flag is. So on an
    // admin path we must still run getAdminData() even when already "logged in":
    // mark the check done for an instant render, then bootstrap in the background
    // (getAdminData opens the socket + re-validates, redirecting to /admin/login
    // if the server session is gone).
    if (isAdminPath && admin_state.logged_in) {
      setHasBeenAuthChecked((prev) => ({ ...prev, checked: true }));
      getAdminData();
      return;
    }

    // Non-admin paths: only a real (already-bootstrapped) user/practice session
    // short-circuits. admin_state.logged_in is intentionally excluded — otherwise
    // the global optimistic flag would skip checkToken on an impersonation /portal
    // tab and the portal guard would kick the impersonated session to /login.
    if (!isAdminPath && (user_state.logged_in || practice_state.logged_in)) {
      setHasBeenAuthChecked((prev) => ({ ...prev, checked: true }));
      return;
    }
    // check if the path is in the ignored list or ignored sub-paths
    if (
      ['ia'].includes(location?.pathname) ||
      [
        'password',
        'invoice',
        'ia',
        'contact',
        'book',
        'terms',
        'rwt-plan',
        'mfa-description',
        'privacy',
        'activate',
        'kiosk',
        'checkin',
        'start-checkin',
        'icons',
        'instant-packet',
        'select-practice',
        'unsubscribe'
      ].includes(path[1]) ||
      ['password', 'ia'].includes(path[2])
    ) {
      setHasBeenAuthChecked((prev) => ({ ...prev, checked: true }));
      return;
      // , 'terminal_integration_test', 'button'
    }
    // check if path is admin or not
    if (path[1]) {
      if (path[1] === 'admin') {
        await getAdminData();
      } else {
        await checkToken(sessionClientId);
        await getPermissions();
        await getModules();
        await getSettings();
      }
    }

    if (location.pathname === '/') {
      // await getAdminData();
      await checkToken();
      await getPermissions();
      await getModules();
      await getSettings();
    }
    setHasBeenAuthChecked((prev) => ({ ...prev, checked: true }));
    return;
  };

  tippy.setDefaultProps({
    theme: 'light'
  });

  const getAdminData = async () => {
    const data = localStorage.getItem('admin-state');
    const resData = await requestApi({ url: '/api/user/check_token', navigate });
    const userPreferences = await requestApi({
      url: '/api/user/preferences/get',
      navigate,
      params: { name: ['sidebar'] }
    });

    if (resData?.token_valid) {
      setActiveSession(true);
      setHasBeenAuthChecked(() => ({ isImpersonate: false, checked: true }));
      setSocketSessionToken(resData?.socketSession);
      connectToSocket({ sessionToken: resData?.socketSession });

      const updatedPreferences = io(userPreferences?.preferences)
        ? Object.entries(userPreferences?.preferences).reduce((acc, [key, value]) => {
            acc[key] = value.value;
            return acc;
          }, {})
        : {};

      if (data) {
        const merged = {
          ...JSON.parse(data),
          ...(resData?.user_data?.mfa_enabled !== undefined
            ? { mfa_enabled: resData.user_data.mfa_enabled }
            : {}),
          logged_in: true,
          impersonating: !!resData?.impersonating,
          preferences: updatedPreferences
        };
        setAdminState(merged);
        localStorage.setItem('admin-state', JSON.stringify(merged));

        getPermissions();
      }
    } else {
      clearStoragePreserveKiosk();
      navigateTo('/admin/login');
    }
  };

  const checkToken = async (sessionClientId) => {
    try {
      const resData = await requestApi({ url: '/api/user/check_token', navigate });
      const { token_valid, user_data, is_admin, socketSession, kiosk } = resData;

      if (kiosk) {
        setKiosk({ isKiosk: true, ...kiosk });
      }

      if (token_valid) {
        setActiveSession(true);
        setSocketSessionToken(socketSession);
        connectToSocket({
          sessionToken: socketSession,
          sessionClientId
        });
        user_data?.display_settings &&
          io(user_data?.display_settings?.appointmentFilters) &&
          setAppointmentFilters(user_data.display_settings.appointmentFilters);

        const displaySettings = _.merge({}, defaultSettings, user_data?.display_settings);
        setUserState({
          ...user_state,
          ...user_data,
          display_settings: displaySettings || defaultSettings,
          authenticated: true,
          logged_in: true
        });

        getPermissions();
        getModules();

        const currPractice = await getCurrentPractice();

        const newUser = {
          id: user_data.id,
          name: `${user_data?.f_name} ${user_data?.l_name}`,
          email: user_data?.email,
          admin: is_admin
        };

        if (currPractice) {
          newUser['practiceId'] = currPractice?.id;
          newUser['practiceName'] = currPractice?.name;
        }

        datadogRum.setUser(newUser);

        // start intercom only for practice staff, not patients
        if (currPractice?.id) {
          Intercom({
            hide_default_launcher: true,
            custom_launcher_selector: '[data-intercom-trigger="true"]',
            app_id: process.env.INTERCOM_APP_ID,
            email: user_data?.email,
            phone: user_data?.phone,
            user_id: user_data?.user_id,
            created_at: user_data?.created_at,
            avatar: user_data?.profile_photo,
            name: `${user_data?.f_name} ${user_data?.l_name}`,
            practiceName: currPractice?.name,
            practiceID: currPractice?.id,
            profileType: user_data?.role,
            ...getSessionReplayAttrs()
          });
        } else if (!is_admin) {
          bootPatientIntercom({
            user: {
              id: user_data?.user_id ?? user_data?.id,
              email: user_data?.email,
              phone: user_data?.phone,
              f_name: user_data?.f_name,
              l_name: user_data?.l_name,
              created_at: user_data?.created_at
            }
          });
        }

        if (is_admin) {
          Honeybadger.setContext({
            user_id: user_data?.user_id,
            user_email: user_data?.email,
            admin: true
          });
        } else {
          Honeybadger.setContext({
            user_id: user_data?.user_id,
            user_email: user_data?.email,
            practice_name: currPractice?.name,
            practice_id: currPractice?.id
          });
        }
      } else {
        clearStoragePreserveKiosk();
        navigateTo('/login');
        shutdown(); // stop intercom
      }
    } catch (error) {
      clearStoragePreserveKiosk();
      Honeybadger.notify(`checkToken: ${error}`);
    }
  };

  const getCurrentPractice = async () => {
    try {
      const resData = await requestApi({ url: '/api/practice/current_practice', navigate });
      const { practice, child_practices, selected_practice_id, aptStatusByPracticeRoles } = resData;

      if (practice) {
        const workingHours = findOpenAndCloseClinicHours(practice?.available_times);
        setCurrentPractice({
          ...currentPractice,
          id: practice.id,
          name: practice.name,
          email: practice.email,
          phone: practice.phone,
          address: practice?.practiceAddress?.fullAddress ?? '',
          header_photo: practice.header_photo,
          timezone: practice.timezone,
          display_settings: { ...defaultPracticeSettings, ...practice?.display_settings },
          working_hours: workingHours,
          appointment_types: practice?.appointment_types,
          care_credit: practice?.care_credit,
          surcharge_percentage: practice?.surcharge_percentage,
          surcharge_enabled: practice?.surcharge_enabled,
          socket_id: practice?.socket_id,
          no_show_fee: practice.no_show_fee,
          tax_id: practice?.tax_id,
          checkin_steps: practice?.checkin_steps,
          fax: practice?.fax,
          displayName: practice?.displayName,
          elab_orders: !!practice?.labsoft_cust_id,
          childPractices: child_practices,
          isMulti: child_practices?.length > 1,
          profession_group_id: practice?.profession_group_id,
          kind: practice?.kind,
          medbill_client: practice?.medbill_client
        });
        if (ia(child_practices)) {
          setOwnerPractices(child_practices);
        }
        if (selected_practice_id) {
          setOwnerSelectedLocation(selected_practice_id);
        }

        setAppointmentStatuses([
          ...(defaultAptStatuses || []),
          ...(aptStatusByPracticeRoles || [])
        ]);
        queryClient.setQueryData(['currentPractice'], resData);
      } else {
        setCurrentPractice({});
        queryClient.removeQueries(['currentPractice']);
      }

      return { id: practice?.id, name: practice?.name };
    } catch (error) {
      Honeybadger.notify(`getCurrentPractice: ${error}`);
    }
  };
  const getPermissions = async () => {
    if (
      permissions?.loaded ||
      (!user_state.logged_in &&
        !admin_state.logged_in &&
        user_state.logged_in === admin_state.logged_in)
    ) {
      return;
    }
    const resData = await requestApi({ url: '/api/permissions/get', navigate });
    const permissions = resData?.permissions;
    if (permissions) {
      setPermissions({ ...permissions, loaded: true });
    }
  };

  const getModules = async () => {
    if (
      modules?.loaded ||
      (!user_state.logged_in &&
        !admin_state.logged_in &&
        user_state.logged_in === admin_state.logged_in)
    ) {
      return;
    }
    const resData = await getPracticeModules(navigate);
    if (resData?.modules) setModules({ ...resData.modules, loaded: true });
  };

  const getSettings = async () => {
    if (
      settings.loaded ||
      (!user_state.logged_in &&
        !admin_state.logged_in &&
        user_state.logged_in === admin_state.logged_in)
    ) {
      return;
    }
    const resData = await requestApi({ url: '/api/practice/settings/get', navigate });
    const loadedSettings = resData?.settings;
    if (loadedSettings) {
      setSettings({ ...clinicalNoteSections, ...loadedSettings?.clinical_notes });
    }
  };

  return <></>;
}
